import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild, computed, effect, inject, input, linkedSignal, ChangeDetectionStrategy } from '@angular/core';
/* import { DecimalPipe } from '@angular/common'; */
import { MatCardModule } from '@angular/material/card';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
/* import { MatSort, MatSortModule } from '@angular/material/sort'; */
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { BaseChartDirective } from 'ng2-charts';
import { ChartConfiguration, ChartData } from 'chart.js';
import { CSV_DOWNLOAD_HINT, DataService } from '../../services/data.service';
import { SettingsService, utcOffsetValue } from '../../services/settings.service';
import { AuthService } from '../../services/auth.service';
import { aggregateStatusByDay, aggregateStatusCounts, statusColor } from '../../shared/scan-stats';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from '@angular/material/icon';
import { ApiService } from '../../services/api.service';
import { Scan } from '../../models/scan.model';
import { NewScan } from '../../models/processedVin/newScan.model';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';

import { toObservable } from '@angular/core/rxjs-interop';
import { skip } from 'rxjs/operators';
import { downloadCsv } from '../../shared/csv';
import { whiteCanvasBackground } from '../../shared/chart-plugins';
import { latestLoad } from '../../shared/latest-load';
import { FilterBarComponent } from '../../shared/filter-bar/filter-bar.component';
import { DataFilterService } from '../../services/data-filter.service';

type StatusKey = 'processingStatusAutoCleared' | 'processingStatusDisconnectedCar';

type DateRange = { start: Date; end: Date };

/** One table request: the range, and the cursor for the page wanted. */
interface TableLoad {
  dateRange: DateRange;
  cursor: string | null;
}

/**
 * Renders the charts + filterable table for a single processing-status field
 * (Auto-Cleared or Disconnected Car). Reused across dashboard tabs.
 */
@Component({
  selector: 'app-status-panel',
  imports: [
    /* DecimalPipe, */
    MatCardModule,
    MatTableModule,
    MatPaginatorModule,
    /* MatSortModule, */
    MatFormFieldModule,
    MatInputModule,
    BaseChartDirective,
    MatToolbarModule,
    MatButtonModule,
    MatIconModule,
    MatProgressBarModule,
    MatTooltipModule,
    FilterBarComponent
],
  templateUrl: './status-panel.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './status-panel.component.scss'
})
export class StatusPanelComponent implements OnInit, AfterViewInit, OnDestroy {
  /** Human label for the status, e.g. 'Auto-Cleared'. */
  readonly label = input.required<string>();
  /** Which scan status field this panel visualizes. */
  readonly statusKey = input.required<StatusKey>();

  readonly data = inject(DataService);
  private readonly settings = inject(SettingsService);
  private readonly api = inject(ApiService);
  readonly auth = inject(AuthService);
  private readonly filters = inject(DataFilterService);
  tableLoading: boolean = false;
  chartLoading: boolean = false;

  readonly csvDownloadHint = CSV_DOWNLOAD_HINT;

  /** Table loads — only the latest counts; see latestLoad. */
  private readonly loadTable = latestLoad<TableLoad>(
    ({ dateRange, cursor }) => this.api.getProccessedVinsPaginated(
      toIsoDate(dateRange.start),
      toIsoDate(dateRange.end),
      utcOffsetValue(this.settings.timezone(), dateRange.start),
      cursor,
      this.data.scanTablePagination().pageSize,
      true // the report reflects the user's selection
    ),
    active => this.tableLoading = active
  );

  /** Chart loads — only the latest counts; see latestLoad. */
  private readonly loadChart = latestLoad<DateRange>(
    dateRange => this.api.getProccessedVinsChart(
      toIsoDate(dateRange.start),
      toIsoDate(dateRange.end),
      utcOffsetValue(this.settings.timezone(), dateRange.start),
      true // the report reflects the user's selection
    ),
    active => this.chartLoading = active
  );

  /* readonly totalScans = this.data.totalScans; */
  readonly timezone = this.settings.timezone;
  readonly processor = this.auth.processorLabel;

  /** Reads the status value for the configured field from a scan. */
  private readonly accessor = (scan: Scan): string => scan.productAndModuleDetails[this.statusKey()];

  statusOf(scan: Scan): string {
    return this.accessor(scan);
  }

  public pieChartType = 'pie' as const;

  /* readonly pieChartData = computed<ChartData<'pie', number[], string>>(() => {
    const breakdown = aggregateStatusCounts(this.data.scans(), this.accessor);
    return {
      labels: breakdown.map(b => b.status),
      datasets: [
        {
          data: breakdown.map(b => b.count),
          backgroundColor: breakdown.map(b => statusColor(b.status))
        }
      ]
    };
  }); */

  /* public pieChartOptions: ChartConfiguration<'pie'>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: 'right' } }
  }; */

  public stackedBarChartType = 'bar' as const;

  /**
   * Held in a field rather than written inline in the template: an array
   * literal there would be a new reference on every change-detection pass,
   * which the chart directive reads as a changed input and re-renders on.
   */
  readonly chartPlugins = [whiteCanvasBackground];

  /* readonly stackedBarChartData = computed<ChartData<'bar'>>(() => {
    const { days, series } = aggregateStatusByDay(this.data.scans(), this.accessor);
    return {
      labels: days.map(day => this.formatDay(day)),
      datasets: series.map(s => ({
        label: s.status,
        data: s.data,
        backgroundColor: statusColor(s.status)
      }))
    };
  }); */

  readonly stackedBarChartData = computed<ChartData<'bar'>>(() => {
    /* const dates = ['Date 1', 'Date 2'];
    const activity = [
      {
        status: 'Completed',
        data: [10, 5],
      },
      {
        status: 'Exception Reported',
        data: [10, 5],
      },
      {
        status: 'Abandoned',
        data: [10, 5],
      },
      {
        status: 'Unsuccessful',
        data: [10, 5],
      },
    ] */
    return {
      labels: this.data.activityByDateChartData().dates.map(day => this.formatDateWithoutYear(day)),
      /* labels: dates, */
      datasets: this.data.activityByDateChartData().activity.map(s => ({
        label: s.status,
        data: s.scans,
        backgroundColor: statusColor(s.status)
      }))
    };
  });

  public stackedBarChartOptions: ChartConfiguration<'bar'>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 16
      }
    },
    plugins: {
      legend: { position: 'bottom' }
    },
    scales: {
      x: {
        stacked: true,
        grid: { display: false }
       },
      y: {
          stacked: true,
          beginAtZero: true,
          grid: {
          color: '#cacaca',
          // Otherwise the tick marks survive as stubs poking out where the
          // axis line used to be.
          drawTicks: false
        },
        }
    }
  };

  readonly rangeLabel = computed(() => {
    const range = this.data.dateRange();
    if (!range) {
      return null;
    }
    const fmt = new Intl.DateTimeFormat(this.settings.locale(), { dateStyle: 'medium' });
    return `${fmt.format(range.start)} – ${fmt.format(range.end)}`;
  });

  readonly latestScanLabel = computed(() => {
    const scans = this.data.scans();
    if (!scans.length) {
      return null;
    }
    let latest = scans[0].activityAndSessionDetails.endDate;
    for (const scan of scans) {
      const end = scan.activityAndSessionDetails.endDate;
      if (end > latest) {
        latest = end;
      }
    }
    return new Intl.DateTimeFormat(this.settings.locale(), {
      dateStyle: 'medium',
      timeStyle: 'short',
      timeZone: this.settings.timezone()
    }).format(new Date(latest));
  });

  // --- Raw scan data table ---

  /* readonly displayedColumns = ['date', 'user', 'vin', 'vehicle', 'site', 'state', 'status', 'duration']; */

  readonly displayedColumns = ['timestamp', 'status', 'vinCode', 'decodedMake', 'decodedModel', 'decodedYear', 'processorSite', 'username', 'emailId', 'inventorySyncStatus', 'inventoryId', 'durationInSeconds', 'controllerId'];

  readonly tableHeaderTooltips = {
    timestamp: "Date and Time the activity was completed",
    status: "Processing Status",
    vinCode: "17-digit vehicle ID",
    decodedMake: "Vehicle manufacturer",
    decodedModel: "Vehicle model",
    decodedYear: "Vehicle manufacture year",
    processorSite: "Name of the site processing the vehicle",
    username: "User who performed the activity",
    emailId: "Email of the user who performed the activity",
    inventorySyncStatus: "Status of activity details sync with inventory provider",
    inventoryId: "Unique ID for Inventory Eg: AASC for Auction; Stock # for Dealer",
    durationInSeconds: "Total time taken to complete",
    controllerId: "ID of the company who owns the vehicle"
  }

  dataSource = new MatTableDataSource<NewScan>([]);

  @ViewChild(MatPaginator) private paginator!: MatPaginator;
  /* @ViewChild(MatSort) private sort!: MatSort; */

  constructor() {

    /* this.dataSource.filterPredicate = (scan, filter) => this.searchText(scan).includes(filter);
    this.dataSource.sortingDataAccessor = (scan, column) => {
      switch (column) {
        case 'date': return scan.activityAndSessionDetails.startDate;
        case 'user': return scan.activityAndSessionDetails.userName;
        case 'vin': return scan.vehiclesPhysicalAttributes.vincode;
        case 'vehicle': return scan.vehiclesPhysicalAttributes.decodedMake;
        case 'site': return scan.siteAndLocationAttributes.userSiteName;
        case 'state': return scan.siteAndLocationAttributes.state;
        case 'status': return this.accessor(scan);
        case 'duration': return scan.activityAndSessionDetails.durationInSeconds;
        default: return '';
      }
    }; */

    /* computed(() => {
      this.dataSource.data = this.data.newScans();
    }); */

    toObservable(this.data.newScans).subscribe(newScans => {
      this.dataSource.data = newScans;
    })

    toObservable(this.data.dateRange).subscribe(dateRange => this.reload(dateRange));

    // Filters are request inputs, so a change has to re-request. skip(1): the
    // initial load is driven by dateRange, and replaying the current filters on
    // mount would double it. Later emissions are always real changes — the
    // applied signal's equality suppresses re-applying an unchanged selection.
    toObservable(this.filters.applied).pipe(skip(1))
      .subscribe(() => this.reload(this.data.dateRange()));

    toObservable(this.settings.timezone).subscribe(timezone => {

      if (this.settings.defaultTimezoneInitialized == true) {
        this.reload(this.data.dateRange());
      } else {
        this.settings.defaultTimezoneInitialized = true;
      }

    })

    this.dataSource.paginator = this.paginator;

  }

  ngOnInit(): void {
    /* this.loadTableData(); */
  }

  ngAfterViewInit(): void {

  }

  ngOnDestroy(): void {
    this.settings.defaultTimezoneInitialized = false;
  }

  applyFilter(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.dataSource.filter = value.trim().toLowerCase();
    this.dataSource.paginator?.firstPage();
  }

  formatDateTime(iso: string): string {
    return new Intl.DateTimeFormat(this.settings.locale(), {
      dateStyle: 'medium',
      timeStyle: 'short',
      timeZone: this.settings.timezone()
    }).format(new Date(iso));
  }

  formatDate(iso: string): string {
    return new Intl.DateTimeFormat(this.settings.locale(), {
      dateStyle: 'medium',
      /* timeStyle: 'short', */
      timeZone: this.settings.timezone()
    }).format(new Date(iso));
  }

  /**
   * A chart bucket as a short axis label.
   *
   * Rebuilt from its date parts and formatted in UTC because a bucket is a bare
   * calendar day, not an instant. Parsing "2026-08-01T00:00:00Z" and rendering
   * it in the browser's zone labelled every bar a day early west of Greenwich.
   */
  formatDateWithoutYear(bucket: string): string {
    const [year, month, day] = bucket.slice(0, 10).split('-').map(Number);
    return new Intl.DateTimeFormat(this.settings.locale(), {
      day: 'numeric',
      month: 'short',
      timeZone: 'UTC'
    }).format(new Date(Date.UTC(year, month - 1, day)));
  }

  formatDuration(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}m ${secs.toString().padStart(2, '0')}s`;
  }

  private formatDay(isoDate: string): string {
    const [year, month, day] = isoDate.split('-').map(Number);
    const date = new Date(Date.UTC(year, month - 1, day));
    return new Intl.DateTimeFormat(this.settings.locale(), {
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC'
    }).format(date);
  }

  private searchText(scan: Scan): string {
    const a = scan.activityAndSessionDetails;
    const v = scan.vehiclesPhysicalAttributes;
    const s = scan.siteAndLocationAttributes;
    return [
      a.startDate, a.userName, a.emailId, a.userType,
      v.vincode, v.regNumber, v.decodedMake, v.decodedModel, v.decodedYear, v.classification,
      s.userSiteName, s.city, s.state,
      this.accessor(scan)
    ].join(' ').toLowerCase();
  }

  paginatorEventHandler(ev: PageEvent) {
    console.log(ev);

    if (ev.previousPageIndex && ev.pageIndex < ev.previousPageIndex) {
      this.loadTableData(this.data.dateRange(), false, false, true);
    } else {
      this.loadTableData(this.data.dateRange(), false, true, false);
    }

  }

  /** Back to the first page, then reload the table and the chart. */
  private reload(dateRange: DateRange | null): void {
    // Left exactly as before: the three report paginators currently share one
    // object, so this reset must not change on its own.
    this.data.setDefaultScanTablePagination();

    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }

    this.loadTableData(dateRange, true, false, false);

    this.loadChartData(dateRange);
  }

  loadTableData(dateRange: DateRange | null, refresh: boolean, next: boolean, previous: boolean) {

    if (dateRange == null) {
      return;
    }

    const pagination = this.data.scanTablePagination();

    this.data.setDefaultNewScans();

    this.loadTable({
      dateRange,
      cursor: refresh ? null : next ? pagination.cursorNext : pagination.cursorPrev
    });

  }

  loadChartData(dateRange: DateRange | null) {

    if (dateRange == null) {
      return;
    }

    this.data.setDefaultActivityByDateChartData();

    this.loadChart(dateRange);

  }

  /** Downloads the chart's data; the file itself is prepared by the DataService. */
  exportChartCSV(): void {

    const file = this.data.activityByDateChartCsvFile();

    if (file) {
      downloadCsv(file.filename, file.content);
    }

  }

  exportInvoiceReportCSV() {

    const dateRange = this.data.dateRange();

    if (dateRange != null) {
      this.api.exportInvoiceReportCSV(
        toIsoDate(dateRange.start),
        toIsoDate(dateRange.end),
        utcOffsetValue(this.settings.timezone(), dateRange.start),
        true // the export matches the table on screen
      );
    }

  }

}

/**
 * A picker date as YYYY-MM-DD. Uses the local calendar parts rather than
 * toISOString(), which converts to UTC first and can shift the day.
 */
function toIsoDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
